import { useState } from 'react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { T, Num, useGT } from 'gt-react';
import { Input } from '@/components/ui/input';
import {
  COLUMN_SEARCH_THRESHOLD,
  matchingColumns,
  type CatalogColumn,
} from './workbookSourceColumnSearch';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

/** A Data Source link's columns, as its schema describes them: closed until opened. */
export function WorkbookSourceColumns({
  columns,
}: {
  columns: readonly CatalogColumn[];
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const shown = matchingColumns(columns, query);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 rounded-sm font-medium text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring">
        {open ? (
          <CaretDownIcon aria-hidden className="size-3.5" />
        ) : (
          <CaretRightIcon aria-hidden className="size-3.5" />
        )}
        <T context="workbook data source columns disclosure">
          Columns (<Num>{columns.length}</Num>)
        </T>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2">
        {columns.length > COLUMN_SEARCH_THRESHOLD && (
          <Input
            type="search"
            className="h-8 text-xs"
            aria-label={gt('Search columns', {
              context: 'workbook data source columns search',
            })}
            placeholder={gt('Search columns', {
              context: 'workbook data source columns search',
            })}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
        {shown.length === 0 ? (
          <p role="status">
            <T context="workbook data source columns no match">
              No column matches the search.
            </T>
          </p>
        ) : (
          <dl className="max-h-64 divide-y divide-border overflow-auto rounded-md border border-border">
            {shown.map((column) => (
              <div key={column.name} className="space-y-0.5 px-2 py-1.5">
                <dt className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-foreground">
                    {column.name}
                  </span>
                  {column.label && <span>{column.label}</span>}
                </dt>
                {column.description && (
                  <dd className="text-muted-foreground">
                    {column.description}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
